import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { toast } from "sonner";
import axios from "axios";
import { apiErrorMessage } from "../lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Sparkles, Loader, Play, Pause, Check, X,
  Upload, Film, RefreshCw, Download, Package, ChevronRight, ChevronDown, ChevronUp,
  Volume2, Zap, Music, FlaskConical, Copy, Settings, Calendar,
  TrendingUp, Crown, Smile, Minimize2, Cpu, MapPin, Eye,
  Share2, ExternalLink, Send, CreditCard, AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safely extract a string message from FastAPI error responses
// (detail can be a string OR a Pydantic v2 array of {type,loc,msg,...})
const apiErrMsg = apiErrorMessage;

// Returns true if this error is an in-app credit shortage (HTTP 402)
const isCreditError = (err) => err?.response?.status === 402;

// Returns true if subscription is required (HTTP 403 with our machine-readable detail)
const isSubscriptionError = (err) => {
  if (err?.response?.status !== 403) return false;
  const d = err?.response?.data?.detail;
  return d === "subscription_required" || d === "subscription_required_for_topup";
};

const EDIT_PLATFORMS = [
  { id: "TikTok", label: "TikTok" },
  { id: "Instagram Reels", label: "Instagram Reels" },
  { id: "YouTube Shorts", label: "YouTube Shorts" },
  { id: "Snapchat", label: "Snapchat" },
  { id: "LinkedIn", label: "LinkedIn" },
  { id: "X / Twitter", label: "X / Twitter" },
];
const EDIT_TONES = [
  { id: "Bold", icon: TrendingUp },
  { id: "Luxury", icon: Crown },
  { id: "Funny", icon: Smile },
  { id: "Minimal", icon: Minimize2 },
  { id: "Tech", icon: Cpu },
  { id: "Local", icon: MapPin },
];

// Voice descriptions/tags are translated via `voices.<id>.desc|tag`
const VOICES = [
  { id: "nova",    label: "Nova",    gender: "F" },
  { id: "shimmer", label: "Shimmer", gender: "F" },
  { id: "coral",   label: "Coral",   gender: "F" },
  { id: "alloy",   label: "Alloy",   gender: "N" },
  { id: "echo",    label: "Echo",    gender: "N" },
  { id: "sage",    label: "Sage",    gender: "N" },
  { id: "ash",     label: "Ash",     gender: "M" },
  { id: "onyx",    label: "Onyx",    gender: "M" },
  { id: "fable",   label: "Fable",   gender: "M" },
];

// Kling v3 Pro max clip = 15s; max video = 30s (2 × 15s calls)
const SORA_MAX_CLIP = 30;

// Labels/descriptions translated via `project.fullAdSteps.<key>`
const FULL_AD_STEPS = [
  { key: "generating_video" },
  { key: "generating_voiceover" },
  { key: "rendering_video" },
];

function computePhase(project) {
  if (!project) return "generating_text";
  // Only show the loading screen when there's NOTHING to display yet.
  // If variants already exist, keep them visible during append/generating.
  if (project.status === "generating" && !project.variants?.length) return "generating_text";
  if (!project.variants?.length) return "text_failed";   // pending (no credits) or failed
  if (!project.selected_variant_id) return "select_variant";
  const v = project.variants.find(v => v.variant_id === project.selected_variant_id);
  if (!v) return "select_variant";
  const fas = v.full_ad_status;
  if (["starting", "generating_video", "generating_voiceover", "rendering_video"].includes(fas)) return "generating_full";
  if (fas === "completed" || v.video_status === "completed") return "ready";
  return "build_ad";
}

// ========== EDIT SETTINGS PANEL ==========
function EditSettingsPanel({ project, onSaved, onCancel }) {
  const { t } = useTranslation();
  const [platforms, setPlatforms] = useState(project.platforms || []);
  const [tone, setTone] = useState(project.tone || "Bold");
  const [saving, setSaving] = useState(false);

  const togglePlatform = (id) =>
    setPlatforms(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);

  const handleSave = async () => {
    if (platforms.length === 0) { toast.error(t("newProject.errors.platforms")); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/projects/${project.project_id}/settings`,
        { platforms, tone }, { withCredentials: true });
      toast.success(t("project.toasts.settingsSaved"));
      onSaved(res.data);
    } catch (err) {
      toast.error(apiErrMsg(err, t("project.toasts.settingsFailed")));
    } finally { setSaving(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-5 mb-6"
      style={{ background: "#161616", border: "1px solid rgba(168,85,247,0.2)" }}
      data-testid="edit-settings-panel"
    >
      <p className="text-xs font-bold uppercase tracking-wider mb-4" style={{ color: "#6b6b6b" }}>{t("newProject.step2.platforms")}</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {EDIT_PLATFORMS.map(p => (
          <button key={p.id} onClick={() => togglePlatform(p.id)}
            data-testid={`edit-platform-${p.id.replace(/\s+/g, "-").toLowerCase()}`}
            className="text-xs px-3 py-1.5 rounded-full font-medium transition-all"
            style={{
              background: platforms.includes(p.id) ? "rgba(147,51,234,0.15)" : "#252525",
              border: `1px solid ${platforms.includes(p.id) ? "#9333ea" : "#333"}`,
              color: platforms.includes(p.id) ? "#c084fc" : "#6b6b6b"
            }}>
            {p.label}
          </button>
        ))}
      </div>

      <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "#6b6b6b" }}>{t("newProject.step3.tone")}</p>
      <div className="grid grid-cols-6 gap-2 mb-5">
        {EDIT_TONES.map(tn => {
          const Icon = tn.icon;
          return (
            <button key={tn.id} onClick={() => setTone(tn.id)}
              data-testid={`edit-tone-${tn.id.toLowerCase()}`}
              className="p-2 rounded-xl flex flex-col items-center gap-1 transition-all"
              style={{
                background: tone === tn.id ? "rgba(147,51,234,0.15)" : "#1c1c1c",
                border: `1px solid ${tone === tn.id ? "#9333ea" : "#252525"}`
              }}>
              <Icon className="w-3.5 h-3.5" style={{ color: tone === tn.id ? "#c084fc" : "#6b6b6b" }} />
              <span className="text-xs font-bold" style={{ color: tone === tn.id ? "#c084fc" : "#6b6b6b" }}>{t(`tones.${tn.id}`)}</span>
            </button>
          );
        })}
      </div>

      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving}
          data-testid="edit-settings-save-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-white transition-all"
          style={{ background: saving ? "#252525" : "linear-gradient(135deg, #7c3aed, #9333ea)" }}>
          {saving ? <><Loader className="w-3.5 h-3.5 animate-spin" /> {t("common.saving")}</> : <><RefreshCw className="w-3.5 h-3.5" /> {t("project.regenerate")} <CreditBadge credits={2} /></>}
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
          style={{ background: "#252525", color: "#6b6b6b" }}
          data-testid="edit-settings-cancel-btn">
          {t("common.cancel")}
        </button>
      </div>
    </motion.div>
  );
}

// Credit cost badge shown on action buttons — font is relative to parent button size
function CreditBadge({ credits }) {
  return (
    <span className="px-1.5 py-0.5 rounded font-bold ml-1.5 inline-flex items-center"
      style={{ background: "rgba(124,58,237,0.3)", color: "#ede9fe", fontSize: "0.8em", lineHeight: 1.4 }}>
      {credits}cr
    </span>
  );
}

// 1 credit = $0.20. Kling v3 Pro: 1 credit per second.
const videoCredits = (durationSecs) => durationSecs;

// ========== PHASE 1: GENERATING TEXT ==========
function GeneratingTextPhase() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-32 text-center">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
        style={{ background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.2)" }}>
        <Sparkles className="w-8 h-8 text-purple-400 animate-pulse" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "Epilogue, sans-serif" }}>
        {t("project.generatingText.title")}
      </h3>
      <p className="text-sm text-zinc-400 mb-6">{t("project.generatingText.subtitle")}</p>
      <div className="w-48 h-1.5 rounded-full overflow-hidden" style={{ background: "#252525" }}>
        <div className="h-1.5 rounded-full animate-pulse"
          style={{ background: "linear-gradient(90deg, #7c3aed, #c084fc)", width: "70%" }} />
      </div>
    </div>
  );
}

function TextFailedPhase({ error, onRetry, isPending }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isBalance = error && (error.toLowerCase().includes("budget") || error.toLowerCase().includes("balance") || error.toLowerCase().includes("insufficient"));

  if (isPending) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center px-6">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
          style={{ background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.2)" }}>
          <CreditCard className="w-8 h-8" style={{ color: "#c084fc" }} />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">{t("project.pending.title")}</h3>
        <p className="text-sm mb-6 max-w-xs" style={{ color: "#6b6b6b" }}>
          {t("project.pending.body")}
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/billing")}
            data-testid="buy-credits-btn"
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm text-white transition-all"
            style={{ background: "linear-gradient(135deg, #7c3aed, #9333ea)" }}>
            <CreditCard className="w-4 h-4" /> {t("dashboard.lowCredit.buy")}
          </button>
          <button
            onClick={onRetry}
            data-testid="retry-text-generation-btn"
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all"
            style={{ background: "#1c1c1c", border: "1px solid #2a2a2a", color: "#a1a1a1" }}>
            <RefreshCw className="w-4 h-4" /> {t("project.pending.tryNow")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-32 text-center px-6">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
        style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)" }}>
        <X className="w-8 h-8 text-red-400" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2">{t("project.failed.title")}</h3>
      {isBalance ? (
        <p className="text-sm mb-6 max-w-xs" style={{ color: "#6b6b6b" }}>
          {t("project.failed.balanceBefore")}{" "}
          <span className="text-purple-400 font-semibold">{t("project.failed.balancePath")}</span>{" "}
          {t("project.failed.balanceAfter")}
        </p>
      ) : (
        <p className="text-sm mb-6 max-w-xs" style={{ color: "#6b6b6b" }}>
          {t("project.failed.body")}
        </p>
      )}
      <button
        onClick={onRetry}
        data-testid="retry-text-generation-btn"
        className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm text-white transition-all"
        style={{ background: "linear-gradient(135deg, #7c3aed, #9333ea)" }}>
        <RefreshCw className="w-4 h-4" /> {t("project.failed.tryAgain")}
      </button>
    </div>
  );
}

// ========== PHASE 2: VARIANT SELECTION ==========
function VariantSelectionCard({ variant, index, onSelect, isCurrentVideo }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const scriptShort = variant.script?.slice(0, 110) || "";
  const isLong = variant.script?.length > 110;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="rounded-2xl p-6 flex flex-col"
      style={{
        background: "#1c1c1c",
        border: isCurrentVideo ? "1px solid rgba(168,85,247,0.45)" : "1px solid #2a2a2a",
        transition: "border-color 0.2s",
      }}
      onMouseOver={e => e.currentTarget.style.borderColor = "rgba(168,85,247,0.35)"}
      onMouseOut={e => e.currentTarget.style.borderColor = isCurrentVideo ? "rgba(168,85,247,0.45)" : "#2a2a2a"}
      data-testid={`variant-choice-${index + 1}`}
    >
      <div className="flex items-center gap-2 self-start mb-4">
        <span className="text-xs font-bold px-3 py-1 rounded-full"
          style={{ background: "rgba(168,85,247,0.1)", color: "#c084fc", border: "1px solid rgba(168,85,247,0.2)" }}>
          {t("project.concept", { n: index + 1 })}
        </span>
        {isCurrentVideo && (
          <span className="text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1"
            style={{ background: "rgba(134,239,172,0.1)", color: "#86efac", border: "1px solid rgba(134,239,172,0.2)" }}
            data-testid={`current-video-badge-${index + 1}`}>
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "#86efac" }} />
            {t("project.currentVideo")}
          </span>
        )}
      </div>

      <h3 className="text-base font-black text-white leading-snug mb-3"
        dir="auto"
        style={{ fontFamily: "Epilogue, sans-serif" }}>
        "{variant.hook}"
      </h3>

      {/* Script — expandable */}
      <div className="mb-3">
        <p className="text-sm leading-relaxed italic"
          dir="auto"
          style={{ color: "#6b6b6b" }}>
          {expanded ? variant.script : (isLong ? `${scriptShort}…` : scriptShort)}
        </p>
        {isLong && (
          <button
            onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
            data-testid={`script-expand-${index + 1}`}
            className="text-xs mt-2 flex items-center gap-1 transition-colors"
            style={{ color: "#7c3aed" }}
            onMouseOver={e => e.currentTarget.style.color = "#c084fc"}
            onMouseOut={e => e.currentTarget.style.color = "#7c3aed"}
          >
            {expanded
              ? <><ChevronUp className="w-3 h-3" /> {t("project.showLess")}</>
              : <><ChevronDown className="w-3 h-3" /> {t("project.readFullScript")}</>}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5">
        {variant.scenes?.slice(0, 3).map((s, i) => (
          <span key={i} className="text-xs px-2 py-0.5 rounded"
            style={{ background: "#252525", color: "#6b6b6b" }}>
            {t("project.scene", { n: s.scene_number ?? s.shot_number ?? (i + 1) })} · {s.duration || s.timecode || ""}
          </span>
        ))}
        {variant.scenes?.length > 3 && (
          <span className="text-xs px-2 py-0.5 rounded"
            style={{ background: "#252525", color: "#6b6b6b" }}>
            {t("project.moreScenes", { count: variant.scenes.length - 3 })}
          </span>
        )}
      </div>

      <button
        onClick={() => onSelect(variant.variant_id)}
        className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ghost-purple mt-auto"
        data-testid={`select-variant-${index + 1}`}
      >
        {t("project.chooseConcept")} <ChevronRight className="w-4 h-4 rtl:rotate-180" />
      </button>
    </motion.div>
  );
}

// ========== MUSIC MOOD SELECTOR ==========
// label/desc translated via `project.music.<key>`
const MUSIC_MOODS = [
  { id: null,            key: "silent" },
  { id: "upbeat",        key: "upbeat" },
  { id: "calm",          key: "calm" },
  { id: "dramatic",      key: "dramatic" },
  { id: "inspirational", key: "inspirational" },
];

function MusicSelector({ value, onChange }) {
  const { t } = useTranslation();
  const [demoLoading, setDemoLoading] = useState(null);
  const [demosCache, setDemosCache] = useState({});
  const [playingMood, setPlayingMood] = useState(null);
  const audioRefs = useRef({});

  const playDemo = async (mood, e) => {
    e.stopPropagation();
    if (playingMood) {
      audioRefs.current[playingMood]?.pause();
      audioRefs.current[playingMood] = null;
      if (playingMood === mood) { setPlayingMood(null); return; }
    }
    if (demosCache[mood]) {
      const audio = new Audio(`data:audio/wav;base64,${demosCache[mood]}`);
      audio.onended = () => setPlayingMood(null);
      audioRefs.current[mood] = audio;
      audio.play();
      setPlayingMood(mood);
      return;
    }
    setDemoLoading(mood);
    try {
      const res = await axios.get(`${API}/music-demo/${mood}`, { withCredentials: true });
      const b64 = res.data.audio_base64;
      setDemosCache(prev => ({ ...prev, [mood]: b64 }));
      const audio = new Audio(`data:audio/wav;base64,${b64}`);
      audio.onended = () => setPlayingMood(null);
      audioRefs.current[mood] = audio;
      audio.play();
      setPlayingMood(mood);
    } catch { toast.error(t("project.toasts.musicDemoFailed")); }
    finally { setDemoLoading(null); }
  };

  return (
    <div className="grid grid-cols-5 gap-2">
      {MUSIC_MOODS.map(m => (
        <div
          key={String(m.id)}
          onClick={() => onChange(m.id)}
          className="relative p-2.5 rounded-xl border cursor-pointer transition-all text-center"
          style={{
            background: value === m.id ? "rgba(147,51,234,0.12)" : "#161616",
            borderColor: value === m.id ? "#9333ea" : "#252525"
          }}
          data-testid={`music-option-${m.id ?? "silent"}`}
        >
          <Music className="w-3.5 h-3.5 mx-auto mb-1" style={{ color: value === m.id ? "#c084fc" : "#6b6b6b" }} />
          <p className="text-xs font-bold leading-tight" style={{ color: value === m.id ? "#c084fc" : "#6b6b6b" }}>{t(`project.music.${m.key}.label`)}</p>
          <p className="text-xs mt-0.5 leading-tight" style={{ color: "#6b6b6b", fontSize: "10px" }}>{t(`project.music.${m.key}.desc`)}</p>
          {m.id && (
            <button
              onClick={e => playDemo(m.id, e)}
              className="absolute bottom-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center transition-colors"
              style={{ background: playingMood === m.id ? "rgba(168,85,247,0.3)" : "rgba(168,85,247,0.1)" }}
              data-testid={`music-demo-${m.id}`}
            >
              {demoLoading === m.id
                ? <Loader className="w-2.5 h-2.5 text-purple-400 animate-spin" />
                : playingMood === m.id
                ? <Pause className="w-2.5 h-2.5 text-purple-400" />
                : <Play className="w-2.5 h-2.5 text-purple-400 ml-0.5" />}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ========== VOICE SELECTOR ==========
function VoiceSelector({ value, onChange }) {
  const { t } = useTranslation();
  const [demoLoading, setDemoLoading] = useState(null);
  const [demosCache, setDemosCache] = useState({});
  const [playingVoice, setPlayingVoice] = useState(null);
  const audioRefs = useRef({});

  const playDemo = async (voiceId, e) => {
    e.stopPropagation();
    if (playingVoice) {
      audioRefs.current[playingVoice]?.pause();
      audioRefs.current[playingVoice] = null;
      if (playingVoice === voiceId) { setPlayingVoice(null); return; }
    }
    if (demosCache[voiceId]) {
      const audio = new Audio(`data:audio/mp3;base64,${demosCache[voiceId]}`);
      audio.onended = () => setPlayingVoice(null);
      audioRefs.current[voiceId] = audio;
      audio.play();
      setPlayingVoice(voiceId);
      return;
    }
    setDemoLoading(voiceId);
    try {
      const res = await axios.get(`${API}/voice-demo/${voiceId}`, { withCredentials: true });
      const b64 = res.data.audio_base64;
      setDemosCache(prev => ({ ...prev, [voiceId]: b64 }));
      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      audio.onended = () => setPlayingVoice(null);
      audioRefs.current[voiceId] = audio;
      audio.play();
      setPlayingVoice(voiceId);
    } catch { toast.error(t("project.toasts.voiceDemoFailed")); }
    finally { setDemoLoading(null); }
  };

  const GENDER_COLORS = { F: "#f472b6", M: "#60a5fa", N: "#a3e635" };

  return (
    <div className="space-y-2">
      {/* Gender legend */}
      <div className="flex items-center gap-4 mb-3">
        {[["F", t("project.voice.feminine")], ["M", t("project.voice.masculine")], ["N", t("project.voice.neutral")]].map(([g, label]) => (
          <div key={g} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: GENDER_COLORS[g] }} />
            <span className="text-xs" style={{ color: "#6b6b6b" }}>{label}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {VOICES.map(v => (
          <div
            key={v.id}
            onClick={() => onChange(v.id)}
            className="relative p-3 rounded-xl border cursor-pointer transition-all"
            style={{
              background: value === v.id ? "rgba(147,51,234,0.12)" : "#161616",
              borderColor: value === v.id ? "#9333ea" : "#252525"
            }}
            data-testid={`voice-option-${v.id}`}
          >
            {/* Gender dot */}
            <span className="absolute top-2 right-2 w-2 h-2 rounded-full"
              style={{ background: GENDER_COLORS[v.gender] }} />
            {/* Selected indicator */}
            {value === v.id && (
              <div className="absolute top-2 left-2 w-2 h-2 rounded-full" style={{ background: "#9333ea" }} />
            )}
            <div className="flex items-start justify-between gap-1 pl-3 pr-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <p className="text-xs font-bold text-white leading-tight">{v.label}</p>
                  <span className="text-xs px-1 py-0 rounded font-medium leading-tight"
                    style={{ background: "rgba(168,85,247,0.15)", color: "#a78bfa", fontSize: "0.6rem" }}>
                    {t(`voices.${v.id}.tag`)}
                  </span>
                </div>
                <p className="text-xs leading-tight" style={{ color: "#6b6b6b" }}>{t(`voices.${v.id}.desc`)}</p>
              </div>
            </div>
            <button
              onClick={e => playDemo(v.id, e)}
              className="absolute bottom-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-colors"
              style={{ background: playingVoice === v.id ? "rgba(168,85,247,0.3)" : "rgba(168,85,247,0.1)" }}
              data-testid={`voice-demo-${v.id}`}
            >
              {demoLoading === v.id
                ? <Loader className="w-3 h-3 text-purple-400 animate-spin" />
                : playingVoice === v.id
                ? <Pause className="w-3 h-3 text-purple-400" />
                : <Play className="w-3 h-3 text-purple-400 ml-0.5" />}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ========== SCENE UPLOAD ZONE ==========
function SceneUploadZone({ scene, variantId, projectId, showOverlay, onOverlayToggle, onRefresh }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const hasUserImage = !!scene.user_image_base64;
  const hasUserVideo = !!scene.user_video_path;
  const hasMedia = hasUserImage || hasUserVideo;

  const handleUpload = async (file) => {
    if (!file) return;
    const isImg = file.type.startsWith("image/");
    const isVid = file.type.startsWith("video/");
    if (!isImg && !isVid) { toast.error(t("project.upload.badType")); return; }

    setUploading(true); setUploadProgress(0);
    try {
      if (isImg) {
        const form = new FormData();
        form.append("variant_id", variantId);
        form.append("scene_number", scene.scene_number);
        form.append("file", file);
        await axios.post(`${API}/projects/${projectId}/upload-scene-image`, form, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(t("project.upload.imageDone"));
      } else {
        const CHUNK = 1 * 1024 * 1024;
        const total = Math.ceil(file.size / CHUNK);
        const initRes = await axios.post(`${API}/projects/${projectId}/upload-video-init`, {
          variant_id: variantId, scene_number: scene.scene_number,
          filename: file.name, total_chunks: total, total_size: file.size
        }, { withCredentials: true });
        const { upload_id } = initRes.data;
        for (let i = 0; i < total; i++) {
          const cf = new FormData();
          cf.append("upload_id", upload_id);
          cf.append("chunk_index", i);
          cf.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
          await axios.post(`${API}/projects/${projectId}/upload-video-chunk`, cf, {
            withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
          });
          setUploadProgress(Math.round(((i + 1) / total) * 100));
        }
        await axios.post(`${API}/projects/${projectId}/upload-video-finalize`, {
          upload_id, variant_id: variantId, scene_number: scene.scene_number
        }, { withCredentials: true });
        toast.success(t("project.upload.videoDone"));
      }
      await onRefresh();
    } catch (err) {
      toast.error(apiErrMsg(err, t("project.upload.failed")));
    } finally { setUploading(false); setUploadProgress(0); }
  };

  const handleRemove = async () => {
    try {
      await axios.delete(`${API}/projects/${projectId}/scene-media`, {
        params: { variant_id: variantId, scene_number: scene.scene_number },
        withCredentials: true
      });
      await onRefresh();
      toast.success(t("project.upload.removed"));
    } catch { toast.error(t("project.upload.removeFailed")); }
  };

  return (
    <div className="rounded-xl p-4 flex flex-col" style={{ background: "#161616", border: "1px solid #252525" }}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <p className="text-xs font-bold text-white">{t("project.scene", { n: scene.scene_number })}</p>
          <p className="text-xs leading-tight mt-0.5 line-clamp-2" style={{ color: "#6b6b6b" }}>
            {scene.description}
          </p>
        </div>
        <span className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
          style={{ background: "#252525", color: "#6b6b6b" }}>
          {scene.duration}
        </span>
      </div>

      {hasMedia ? (
        <div className="relative rounded-lg overflow-hidden mb-3"
          style={{ background: "#1c1c1c", minHeight: 80 }}>
          {hasUserImage ? (
            <img
              src={`data:image/jpeg;base64,${scene.user_image_base64}`}
              className="w-full rounded-lg object-cover" style={{ maxHeight: 120 }} alt="scene"
            />
          ) : (
            <div className="flex items-center gap-2 py-5 px-3 justify-center">
              <Film className="w-5 h-5" style={{ color: "#c084fc" }} />
              <span className="text-xs text-zinc-400 font-medium">{t("project.upload.clipReady")}</span>
            </div>
          )}
          <button
            onClick={handleRemove}
            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: "rgba(0,0,0,0.7)" }}
            data-testid={`remove-media-${scene.scene_number}`}
          >
            <X className="w-3 h-3 text-white" />
          </button>
          <div className="absolute bottom-1.5 left-1.5 text-xs px-1.5 py-0.5 rounded"
            style={{ background: "rgba(0,0,0,0.7)", color: "#a0a0a0" }}>
            {hasUserImage ? t("project.upload.yourImage") : t("project.upload.yourVideo")}
          </div>
        </div>
      ) : uploading ? (
        <div className="rounded-lg flex flex-col items-center justify-center p-5 mb-3"
          style={{ background: "#1c1c1c", border: "1px dashed #333" }}>
          <Loader className="w-4 h-4 text-purple-400 animate-spin mb-2" />
          <p className="text-xs text-zinc-400">{t("project.upload.uploading", { percent: uploadProgress })}</p>
          {uploadProgress > 0 && (
            <div className="w-full mt-2 h-0.5 rounded-full overflow-hidden" style={{ background: "#252525" }}>
              <div className="h-0.5 rounded-full transition-all"
                style={{ background: "#9333ea", width: `${uploadProgress}%` }} />
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files[0]); }}
          className="rounded-lg flex flex-col items-center justify-center p-4 cursor-pointer mb-3 transition-all"
          style={{ background: "#1c1c1c", border: `1px dashed ${dragOver ? "#9333ea" : "#333"}` }}
          data-testid={`scene-upload-${scene.scene_number}`}
        >
          <Upload className="w-4 h-4 mb-1" style={{ color: dragOver ? "#c084fc" : "#6b6b6b" }} />
          <p className="text-xs text-zinc-400">{t("project.upload.prompt")}</p>
          <p className="text-xs mt-0.5" style={{ color: "#6b6b6b" }}>{t("project.upload.aiIfEmpty")}</p>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*,video/*" className="hidden"
        onChange={e => handleUpload(e.target.files[0])} />

      <div className="flex items-center justify-between mt-auto pt-2"
        style={{ borderTop: "1px solid #252525" }}>
        <p className="text-xs" style={{ color: "#6b6b6b" }}>{t("project.upload.textOverlay")}</p>
        <button
          onClick={() => onOverlayToggle(scene.scene_number, !showOverlay)}
          className="relative w-8 h-4 rounded-full transition-colors flex-shrink-0"
          style={{ background: showOverlay ? "#9333ea" : "#2a2a2a" }}
          data-testid={`overlay-toggle-${scene.scene_number}`}
        >
          <div className="absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all"
            style={{ [showOverlay ? "right" : "left"]: "2px" }} />
        </button>
      </div>
      {showOverlay && scene.overlay_text && (
        <p className="text-xs mt-1.5 italic truncate" style={{ color: "#6b6b6b" }}>
          "{scene.overlay_text}"
        </p>
      )}
    </div>
  );
}

// ========== PHASE 3: AD BUILDER ==========
function AdBuilderPhase({ variant, project, onGenerate, onBack, onRefresh }) {
  const { t } = useTranslation();
  const [hook, setHook] = useState(variant.hook || "");
  const [script, setScript] = useState(variant.script || "");
  const [voice, setVoice] = useState("nova");
  const [veoPrompt, setVeoPrompt] = useState("");
  const [musicMood, setMusicMood] = useState(null);
  const [burnCaptions, setBurnCaptions] = useState(false);
  const [videoDuration, setVideoDuration] = useState(10);

  // Normalize scene_number: v2 cinematic format uses shot_number; ensure both exist
  const scenes = (variant.scenes || []).map((s, i) => ({
    ...s,
    scene_number: s.scene_number ?? s.shot_number ?? (i + 1),
  }));

  const [overlays, setOverlays] = useState(
    Object.fromEntries(scenes.map(s => [s.scene_number, true]))
  );
  const [generating, setGenerating] = useState(false);
  const hasFailed = variant.full_ad_status === "failed";

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      await onGenerate({
        variant_id: variant.variant_id,
        voice,
        script,
        hook,
        veo_custom_prompt: veoPrompt.trim() || null,
        music_mood: musicMood,
        burn_captions: burnCaptions,
        video_duration: videoDuration,
        scene_settings: scenes.map((s, i) => {
          const sNum = s.scene_number ?? s.shot_number ?? (i + 1);
          return { scene_number: sNum, show_overlay: overlays[sNum] ?? true };
        })
      });
    } catch (err) { console.error("Full ad generation request failed:", err); /* surfaced by parent */ }
    finally { setGenerating(false); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onBack}
        className="flex items-center gap-2 text-sm mb-6 transition-colors hover:text-white"
        style={{ color: "#6b6b6b" }}
        data-testid="back-to-selection">
        <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("project.chooseDifferent")}
      </button>

      {hasFailed && (
        <div className="rounded-xl p-4 mb-6 flex items-start gap-3"
          style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)" }}
          data-testid="generation-failed-banner">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#f87171" }} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-400 mb-1">{t("project.builder.failedTitle")}</p>
            <p className="text-xs leading-relaxed" style={{ color: "#fca5a5" }}>
              {variant.full_ad_error || t("project.builder.failedBody")}
            </p>
          </div>
        </div>
      )}


      {/* Hook */}
      <div className="mb-5">
        <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: "#6b6b6b" }}>
          {t("project.builder.hook")}
        </label>
        <textarea value={hook} onChange={e => setHook(e.target.value)} rows={2}
          data-testid="edit-hook"
          className="w-full text-sm text-white leading-relaxed rounded-xl p-3 resize-none focus:outline-none focus:ring-1 focus:ring-purple-500"
          style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }} />
      </div>

      {/* Script + live duration mapping */}
      <div className="mb-6">
        <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: "#6b6b6b" }}>
          {t("project.builder.script")}
        </label>
        <textarea value={script} onChange={e => setScript(e.target.value)} rows={4}
          data-testid="edit-script"
          className="w-full text-sm text-white leading-relaxed rounded-xl p-3 resize-none focus:outline-none focus:ring-1 focus:ring-purple-500"
          style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }} />

        {/* Live duration mapping — responds to the Video Length slider below */}
        {(() => {
          const WPS = 2.85;  // OpenAI tts-1 average speech rate
          const maxWords = Math.floor(videoDuration * WPS);
          const words = script.trim().split(/\s+/).filter(Boolean);
          const wordCount = words.length;
          const estSec = wordCount / WPS;
          const gap = videoDuration - estSec;
          const fits = Math.abs(gap) <= 0.7;
          const isOverflow = wordCount > maxWords;
          const isUnderfill = !isOverflow && !fits && gap > 0;

          const statusColor = fits ? "#86efac" : "#fbbf24";
          const statusBg    = fits ? "rgba(134,239,172,0.08)" : "rgba(251,191,36,0.08)";
          const statusLabel = fits
            ? t("project.fit.perfect", { seconds: videoDuration })
            : isOverflow
              ? t("project.fit.overflow", { seconds: (estSec - videoDuration).toFixed(1) })
              : t("project.fit.silence", { seconds: gap.toFixed(1) });

          return (
            <>
              <div className="flex items-center justify-between mt-2 text-[10px]" style={{ color: "#6b6b6b" }}>
                <span data-testid="adbuilder-duration-stat">
                  <span style={{ color: "#a0a0a0" }}>{wordCount}</span> {t("project.fit.words")} ·
                  ~<span style={{ color: "#a0a0a0" }}>{estSec.toFixed(1)}s</span> {t("project.fit.audio")} ·
                  {t("project.fit.target")} <span style={{ color: "#a0a0a0" }}>{videoDuration}s</span>
                </span>
              </div>

              <div
                className="mt-2 px-3 py-1.5 rounded-lg text-[10px] font-semibold flex items-center gap-1.5"
                style={{ background: statusBg, color: statusColor, border: `1px solid ${statusColor}33` }}
                data-testid="adbuilder-fit-status">
                {fits ? <Check className="w-3 h-3" /> : <span style={{ fontSize: "0.7rem" }}>⚠</span>}
                {statusLabel}
              </div>

              {wordCount > 0 && (isOverflow || isUnderfill) && (
                <div
                  className="mt-2 p-3 rounded-xl text-sm leading-relaxed"
                  style={{ background: "#111", border: "1px dashed #2a2a2a" }}
                  data-testid="adbuilder-mapping-preview">
                  <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: "#6b6b6b" }}>
                    {t("project.fit.mappedTo", { seconds: videoDuration })}
                  </p>
                  {isOverflow ? (
                    <p dir="auto">
                      <span style={{ color: "#fff" }}>{words.slice(0, maxWords).join(" ")}</span>
                      <span style={{ color: "#fbbf24", padding: "0 6px", fontWeight: 700 }}>
                        {" │ "}<span style={{ fontSize: "0.65rem", letterSpacing: "0.05em" }}>{t("project.fit.cutoff", { seconds: videoDuration })}</span>{" │ "}
                      </span>
                      <span style={{ color: "#555", textDecoration: "line-through", textDecorationColor: "#444" }}>
                        {words.slice(maxWords).join(" ")}
                      </span>
                    </p>
                  ) : (
                    <p dir="auto">
                      <span style={{ color: "#fff" }}>{script.trim()}</span>
                      <span
                        className="inline-block ml-2 px-2 py-0.5 rounded-full"
                        style={{ background: "rgba(251,191,36,0.12)", color: "#fbbf24", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.05em" }}>
                        {t("project.fit.silenceTag", { seconds: gap.toFixed(1) })}
                      </span>
                    </p>
                  )}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Video Duration — moved up directly under Voiceover Script so the user
          can see the live highlight respond as they pick a length */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: "#6b6b6b" }}>
            {t("project.builder.videoLength")}
          </label>
          <span className="text-lg font-black text-white">{videoDuration}s
            <span className="text-xs font-normal ml-1" style={{ color: "#6b6b6b" }}>
              · {t(`project.durationLabels.${videoDuration <= 5 ? "quick" : videoDuration <= 10 ? "standard" : videoDuration <= 15 ? "extended" : videoDuration <= 20 ? "full" : videoDuration <= 25 ? "long" : "max"}`)}
            </span>
          </span>
        </div>
        <div className="relative px-1">
          <input
            type="range" min={5} max={30} step={5}
            value={videoDuration}
            onChange={e => setVideoDuration(Number(e.target.value))}
            data-testid="duration-range-slider"
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #9333ea ${((videoDuration - 5) / 25) * 100}%, #252525 ${((videoDuration - 5) / 25) * 100}%)`,
              accentColor: "#9333ea"
            }}
          />
          <div className="flex justify-between mt-2">
            {[5, 10, 15, 20, 25, 30].map(v => (
              <button key={v} onClick={() => setVideoDuration(v)}
                className="text-xs transition-colors"
                data-testid={`duration-${v}s`}
                style={{ color: videoDuration === v ? "#c084fc" : "#6b6b6b" }}>
                {v}s
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Scenes */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs font-bold uppercase tracking-wider" style={{ color: "#6b6b6b" }}>
            {t("project.builder.scenesLabel")}
          </label>
          <span className="text-xs" style={{ color: "#6b6b6b" }}>
            {t("project.builder.sceneCount", { count: scenes.length })}
          </span>
        </div>
        <div className={`grid gap-3 ${scenes.length <= 2 ? "grid-cols-2" : "grid-cols-3"}`}>
          {scenes.map(scene => (
            <SceneUploadZone
              key={scene.scene_number}
              scene={scene}
              variantId={variant.variant_id}
              projectId={project.project_id}
              showOverlay={overlays[scene.scene_number] ?? true}
              onOverlayToggle={(sn, val) => setOverlays(prev => ({ ...prev, [sn]: val }))}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      </div>

      {/* Voice */}
      <div className="mb-8">
        <label className="text-xs font-bold uppercase tracking-wider mb-3 block" style={{ color: "#6b6b6b" }}>
          {t("project.builder.voiceLabel")}
        </label>
        <VoiceSelector value={voice} onChange={setVoice} />
      </div>

      {/* Background Music */}
      <div className="mb-8">
        <label className="text-xs font-bold uppercase tracking-wider mb-3 block" style={{ color: "#6b6b6b" }}>
          {t("project.builder.music")}
        </label>
        <MusicSelector value={musicMood} onChange={setMusicMood} />
      </div>

      {/* Burn Captions Toggle */}
      <div className="flex items-center justify-between mb-6 p-4 rounded-xl"
        style={{ background: "#161616", border: "1px solid #252525" }}>
        <div>
          <p className="text-sm font-bold text-white">{t("project.builder.burnCaptions")} <span className="text-xs font-normal ml-1" style={{ color: "#6b6b6b" }}>({t("common.optional").toLowerCase()})</span></p>
          <p className="text-xs mt-0.5" style={{ color: "#6b6b6b" }}>
            {t("project.builder.burnCaptionsHint")}
          </p>
        </div>
        <button
          onClick={() => setBurnCaptions(b => !b)}
          data-testid="burn-captions-toggle"
          className="relative rounded-full transition-all flex-shrink-0"
          style={{
            background: burnCaptions ? "#9333ea" : "#252525",
            width: 40, height: 22,
            border: `1px solid ${burnCaptions ? "#7c3aed" : "#333"}`
          }}>
          <span className="absolute top-0.5 transition-all rounded-full bg-white"
            style={{ width: 16, height: 16, left: burnCaptions ? 22 : 3 }} />
        </button>
      </div>

      {/* Video Direction */}
      <div className="mb-8">
        <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: "#6b6b6b" }}>
          {t("project.builder.direction")} <span style={{ color: "#6b6b6b", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>({t("common.optional").toLowerCase()})</span>
        </label>
        <textarea
          value={veoPrompt}
          onChange={e => setVeoPrompt(e.target.value)}
          rows={2}
          placeholder={t("project.builder.directionPlaceholder")}
          data-testid="veo-custom-prompt"
          className="w-full text-sm text-white leading-relaxed rounded-xl p-3 resize-none focus:outline-none focus:ring-1 focus:ring-purple-500"
          style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}
        />
        <p className="text-xs mt-1.5" style={{ color: "#6b6b6b" }}>
          {t("project.builder.directionHint")}
        </p>
      </div>

      {/* Generate CTA */}
      <button
        onClick={handleGenerate}
        disabled={generating}
        data-testid="generate-full-ad-btn"
        className="w-full py-4 rounded-2xl font-black text-base flex items-center justify-center gap-3 transition-all text-white"
        style={{
          background: generating ? "#252525" : "linear-gradient(135deg, #7c3aed, #9333ea)",
          opacity: generating ? 0.7 : 1
        }}
      >
        {generating
          ? <><Loader className="w-5 h-5 animate-spin" /> {t("project.builder.starting")}</>
          : <><Zap className="w-5 h-5" /> {t("project.builder.generateFull")} <CreditBadge credits={videoCredits(videoDuration)} /></>}
      </button>
      <p className="text-xs text-center mt-2" style={{ color: "#6b6b6b" }}>
        {t("project.builder.allInOne")}
      </p>
    </div>
  );
}

// ========== PHASE 4: GENERATING FULL AD ==========
function GeneratingFullPhase({ fullAdStatus, startedAt }) {
  const { t } = useTranslation();
  const mountTs = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const parsed = startedAt ? new Date(startedAt).getTime() : NaN;
    const base = Number.isFinite(parsed) ? parsed : mountTs.current;
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - base) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const fmtElapsed = elapsed >= 60
    ? `${Math.floor(elapsed / 60)}m ${String(elapsed % 60).padStart(2, "0")}s`
    : `${elapsed}s`;

  const getState = (stepKey) => {
    const order = ["generating_video", "generating_voiceover", "rendering_video"];
    const curIdx = fullAdStatus === "starting" ? 0 : order.indexOf(fullAdStatus);
    const stepIdx = order.indexOf(stepKey);
    if (curIdx === -1) return "pending";
    if (stepIdx < curIdx) return "done";
    if (stepIdx === curIdx) return "active";
    return "pending";
  };

  const activeStep = FULL_AD_STEPS.find(s => getState(s.key) === "active");

  return (
    <div className="flex flex-col items-center justify-center py-16" data-testid="generating-full-phase">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-8"
        style={{ background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.2)" }}>
        <Zap className="w-8 h-8 text-purple-400 animate-pulse" />
      </div>
      <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "Epilogue, sans-serif" }}>
        {t("project.generatingFull.title")}
      </h3>
      <p className="text-sm text-zinc-400 mb-4 text-center max-w-md">
        {t("project.generatingFull.bodyBefore")} <span className="text-white font-semibold">{t("project.generatingFull.eta")}</span> {t("project.generatingFull.bodyAfter")}
      </p>
      <div className="flex items-center gap-2 mb-10 px-4 py-2 rounded-full"
        style={{ background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.25)" }}
        data-testid="generation-elapsed-timer">
        <Loader className="w-3.5 h-3.5 text-purple-400 animate-spin" />
        <span className="text-sm font-bold text-purple-300 tabular-nums">
          {activeStep ? t(`project.fullAdSteps.${activeStep.key}.label`) : t("project.generatingFull.working")} · {t("project.generatingFull.elapsed", { time: fmtElapsed })}
        </span>
      </div>
      <div className="w-full max-w-sm space-y-3">
        {FULL_AD_STEPS.map((step, i) => {
          const state = getState(step.key);
          return (
            <div key={step.key}
              className="flex items-center gap-4 rounded-xl p-4 transition-all"
              style={{
                background: "#1c1c1c",
                border: `1px solid ${state === "active" ? "rgba(168,85,247,0.3)" : state === "done" ? "rgba(134,239,172,0.2)" : "#252525"}`
              }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: state === "done" ? "rgba(134,239,172,0.1)" : state === "active" ? "rgba(168,85,247,0.15)" : "#252525" }}>
                {state === "done"
                  ? <Check className="w-4 h-4 text-green-400" />
                  : state === "active"
                  ? <Loader className="w-4 h-4 text-purple-400 animate-spin" />
                  : <span className="text-xs font-bold" style={{ color: "#6b6b6b" }}>{i + 1}</span>}
              </div>
              <div>
                <p className={`text-sm font-bold ${state === "done" ? "text-green-400" : state === "active" ? "text-white" : "text-zinc-400"}`}>
                  {t(`project.fullAdSteps.${step.key}.label`)}
                </p>
                <p className="text-xs text-zinc-400">{t(`project.fullAdSteps.${step.key}.desc`)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ========== PUBLISH MODAL (P3 - Social Media) ==========
const SOCIAL_PLATFORMS = [
  {
    id: "TikTok", icon: "T", color: "#ff0050", bg: "rgba(255,0,80,0.1)",
    charLimit: 2200, uploadUrl: "https://www.tiktok.com/upload",
    deepLink: "tiktok://",
  },
  {
    id: "Instagram Reels", icon: "IG", color: "#e1306c", bg: "rgba(225,48,108,0.1)",
    charLimit: 2200, uploadUrl: "https://www.instagram.com/create/reels/",
    deepLink: "instagram://",
  },
  {
    id: "YouTube Shorts", icon: "YT", color: "#ff0000", bg: "rgba(255,0,0,0.1)",
    charLimit: 5000, uploadUrl: "https://studio.youtube.com/",
    deepLink: "https://studio.youtube.com/",
  },
  {
    id: "Snapchat", icon: "SC", color: "#fffc00", bg: "rgba(255,252,0,0.1)",
    charLimit: 250, uploadUrl: "https://www.snapchat.com/",
    deepLink: "snapchat://",
  },
  {
    id: "Facebook", icon: "FB", color: "#1877f2", bg: "rgba(24,119,242,0.1)",
    charLimit: 63206, uploadUrl: "https://www.facebook.com/",
    deepLink: "fb://",
  },
  {
    id: "LinkedIn", icon: "in", color: "#0a66c2", bg: "rgba(10,102,194,0.1)",
    charLimit: 3000, uploadUrl: "https://www.linkedin.com/feed/",
    deepLink: "linkedin://",
  },
  {
    id: "X / Twitter", icon: "X", color: "#e7e9ea", bg: "rgba(231,233,234,0.08)",
    charLimit: 280, uploadUrl: "https://twitter.com/compose/tweet",
    deepLink: "twitter://",
  },
];

function PublishModal({ project, variant, projectId, onClose }) {
  const { t } = useTranslation();
  const API = process.env.REACT_APP_BACKEND_URL;
  const [platform, setPlatform] = useState(SOCIAL_PLATFORMS[0].id);
  const [step, setStep] = useState("platform"); // "platform" | "guide"
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const videoUrl = `${API}/api/projects/${projectId}/download-video/${variant.variant_id}`;
  const pInfo = SOCIAL_PLATFORMS.find(p => p.id === platform);

  const activeTags = variant.hashtags || [];
  const fullCaption = `${variant.captions || ""}\n\n${activeTags.join(" ")}`.trim();
  const charCount = fullCaption.length;
  const overLimit = pInfo && charCount > pInfo.charLimit;

  const handleProceed = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/social/publish-intent`, {
        project_id: project.project_id,
        variant_id: variant.variant_id,
        platform,
        caption: variant.captions,
        hashtags: activeTags
      }, { withCredentials: true });
      setShareData(res.data);
      setStep("guide");
    } catch (err) {
      toast.error(apiErrMsg(err, t("project.publish.prepareFailed")));
    } finally { setLoading(false); }
  };

  const copyCaption = () => {
    navigator.clipboard.writeText(shareData?.caption || fullCaption);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadVideo = () => {
    const a = document.createElement("a");
    a.href = videoUrl; a.download = `${project.business_name || "ad"}-video.mp4`; a.click();
    toast.success(t("project.toasts.downloading"));
  };

  const handleWebShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: variant.hook || "My Ad", text: fullCaption }); }
      catch (err) {
        // AbortError is expected when the user dismisses the share sheet — ignore quietly.
        if (err?.name !== "AbortError") console.error("Web Share failed:", err);
      }
    } else {
      copyCaption();
      toast.info(t("project.publish.copiedManual"));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-sm rounded-2xl overflow-hidden"
        style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}
        data-testid="publish-modal">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: "1px solid #252525" }}>
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4" style={{ color: "#c084fc" }} />
            <h3 className="text-sm font-bold text-white">
              {step === "platform" ? t("project.publish.title") : t("project.publish.postTo", { platform })}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" style={{ color: "#6b6b6b" }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 max-h-[80vh] overflow-y-auto">
          {step === "platform" ? (
            <>
              <p className="text-xs mb-4" style={{ color: "#6b6b6b" }}>
                {t("project.publish.choose")}
              </p>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {SOCIAL_PLATFORMS.map(p => (
                  <button key={p.id} onClick={() => setPlatform(p.id)}
                    data-testid={`publish-platform-${p.id.replace(/[\s/]+/g, "-").toLowerCase()}`}
                    className="flex items-center gap-2.5 p-3 rounded-xl border transition-all text-left"
                    style={{
                      background: platform === p.id ? p.bg : "#161616",
                      borderColor: platform === p.id ? p.color : "#252525"
                    }}>
                    <span className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0"
                      style={{ background: p.bg, color: p.color, border: `1px solid ${p.color}30` }}>
                      {p.icon}
                    </span>
                    <span className="text-xs font-semibold text-white leading-tight">{p.id}</span>
                  </button>
                ))}
              </div>

              {/* Platform tip */}
              {pInfo && (
                <p className="text-xs px-3 py-2 rounded-lg mb-4" style={{ background: "#161616", color: "#6b6b6b", border: "1px solid #252525" }}>
                  {t(`project.publish.tips.${pInfo.id}`)}
                </p>
              )}

              {/* Character count preview */}
              <div className="flex items-center justify-between mb-4 px-1">
                <span className="text-xs" style={{ color: "#6b6b6b" }}>{t("project.publish.captionLength")}</span>
                <span className="text-xs font-mono" style={{ color: overLimit ? "#f87171" : "#6b6b6b" }}>
                  {charCount} / {pInfo?.charLimit?.toLocaleString()}
                </span>
              </div>

              <button onClick={handleProceed} disabled={loading}
                data-testid="publish-proceed-btn"
                className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
                style={{
                  background: loading ? "#252525" : `linear-gradient(135deg, #7c3aed, #9333ea)`,
                  color: loading ? "#6b6b6b" : "white"
                }}>
                {loading ? <><Loader className="w-4 h-4 animate-spin" /> {t("project.publish.preparing")}</> : <><Send className="w-4 h-4" /> {t("project.publish.getGuide")}</>}
              </button>
            </>
          ) : (
            <>
              {/* Steps */}
              <div className="space-y-2 mb-4">
                {shareData?.steps?.map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                      style={{ background: "rgba(147,51,234,0.15)", color: "#c084fc", border: "1px solid rgba(147,51,234,0.3)" }}>
                      {i + 1}
                    </span>
                    <p className="text-sm text-white leading-snug">{s}</p>
                  </div>
                ))}
              </div>

              {/* Caption box */}
              {shareData?.caption && (
                <div className="rounded-xl p-3 mb-4" style={{ background: "#161616", border: "1px solid #252525" }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-bold uppercase tracking-wider" style={{ color: "#6b6b6b" }}>{t("project.publish.captionHashtags")}</p>
                    <button onClick={copyCaption}
                      className="text-xs flex items-center gap-1 transition-colors"
                      style={{ color: copied ? "#86efac" : "#c084fc" }}
                      data-testid="publish-copy-caption">
                      <Copy className="w-3 h-3" /> {copied ? t("common.copied") : t("common.copy")}
                    </button>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: "#6b6b6b" }}>
                    {shareData.caption.slice(0, 140)}{shareData.caption.length > 140 ? "..." : ""}
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 mb-2">
                <button onClick={downloadVideo}
                  data-testid="publish-download-btn"
                  className="flex-1 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
                  style={{ background: "linear-gradient(135deg, #7c3aed, #9333ea)", color: "white" }}>
                  <Download className="w-4 h-4" /> {t("common.download")}
                </button>
                <a href={pInfo?.uploadUrl || shareData?.share_url} target="_blank" rel="noopener noreferrer"
                  data-testid="publish-open-platform-btn"
                  className="flex-1 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
                  style={{ background: "#161616", border: `1px solid ${pInfo?.color || "#2a2a2a"}30`, color: pInfo?.color || "#c084fc" }}>
                  <ExternalLink className="w-4 h-4" /> {t("project.publish.open", { platform: platform.split(" ")[0] })}
                </a>
              </div>

              {'share' in navigator && (
                <button onClick={handleWebShare}
                  className="w-full py-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all"
                  style={{ background: "#161616", border: "1px solid #252525", color: "#6b6b6b" }}>
                  <Share2 className="w-3 h-3" /> {t("project.publish.shareDevice")}
                </button>
              )}

              <button onClick={() => setStep("platform")}
                className="w-full mt-3 text-xs transition-colors" style={{ color: "#6b6b6b" }}>
                {t("project.publish.chooseDifferent")}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ========== SCHEDULE MODAL ==========
const SCHEDULE_PLATFORMS = ["TikTok", "Instagram Reels", "YouTube Shorts", "Snapchat", "Facebook", "LinkedIn", "X / Twitter"];

function ScheduleModal({ project, variant, onClose, onScheduled }) {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState(project.platforms?.[0] || "TikTok");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [saving, setSaving] = useState(false);

  const handleSchedule = async () => {
    if (!date) { toast.error(t("project.schedule.pickDate")); return; }
    const scheduledAt = new Date(`${date}T${time}:00`).toISOString();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/schedule`, {
        project_id: project.project_id,
        variant_id: variant.variant_id,
        platform,
        scheduled_at: scheduledAt,
        caption: variant.captions || "",
        hashtags: variant.hashtags || []
      }, { withCredentials: true });
      toast.success(t("project.schedule.success", { platform, date, time }));
      onScheduled(res.data);
      onClose();
    } catch (err) {
      toast.error(apiErrMsg(err, t("project.schedule.failed")));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-sm rounded-2xl p-6"
        style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}
        data-testid="schedule-modal">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" style={{ color: "#c084fc" }} />
            <h3 className="text-base font-bold text-white">{t("project.schedule.title")}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" style={{ color: "#6b6b6b" }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mb-4">
          <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: "#6b6b6b" }}>{t("project.schedule.platform")}</label>
          <div className="flex flex-wrap gap-2">
            {SCHEDULE_PLATFORMS.map(p => (
              <button key={p} onClick={() => setPlatform(p)}
                data-testid={`schedule-platform-${p.replace(/\s+/g, "-").toLowerCase()}`}
                className="text-xs px-3 py-1.5 rounded-full font-medium transition-all"
                style={{
                  background: platform === p ? "rgba(147,51,234,0.15)" : "#252525",
                  border: `1px solid ${platform === p ? "#9333ea" : "#333"}`,
                  color: platform === p ? "#c084fc" : "#6b6b6b"
                }}>
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: "#6b6b6b" }}>{t("project.schedule.date")}</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              data-testid="schedule-date-input"
              className="w-full text-sm text-white rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-purple-500"
              style={{ background: "#161616", border: "1px solid #2a2a2a" }} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: "#6b6b6b" }}>{t("project.schedule.time")}</label>
            <input type="time" value={time} onChange={e => setTime(e.target.value)}
              data-testid="schedule-time-input"
              className="w-full text-sm text-white rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-purple-500"
              style={{ background: "#161616", border: "1px solid #2a2a2a" }} />
          </div>
        </div>

        <button onClick={handleSchedule} disabled={saving}
          data-testid="schedule-confirm-btn"
          className="w-full py-3 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-all"
          style={{ background: saving ? "#252525" : "linear-gradient(135deg, #7c3aed, #9333ea)" }}>
          {saving ? <><Loader className="w-4 h-4 animate-spin" /> {t("project.schedule.scheduling")}</> : <><Calendar className="w-4 h-4" /> {t("project.schedule.title")}</>}
        </button>
      </motion.div>
    </div>
  );
}

// ========== PHASE 5: READY ==========
function ReadyPhase({ project, variant, onStartOver, projectId, onRegenVideo, onRefresh, handleCreditErr }) {
  const { t } = useTranslation();
  const [downloadingPack, setDownloadingPack] = useState(false);
  const [abLoading, setAbLoading] = useState(false);
  const [abVariants, setAbVariants] = useState([]);
  const [abApplying, setAbApplying] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenDirection, setRegenDirection] = useState("");
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenDuration, setRegenDuration] = useState(variant.video_duration || 10);
  const [regenVoice, setRegenVoice] = useState(variant.voiceover_voice || "nova");
  const [regenHook, setRegenHook] = useState(variant.hook || "");
  const [regenScript, setRegenScript] = useState(variant.script || "");
  const [regenMusicMood, setRegenMusicMood] = useState(null);
  const [regenBurnCaptions, setRegenBurnCaptions] = useState(false);

  // Inline caption + hashtag editing
  const [captionText, setCaptionText] = useState(variant.captions || "");
  const [hashtagList, setHashtagList] = useState(variant.hashtags || []);
  const [hashtagInput, setHashtagInput] = useState("");
  const [captionSaving, setCaptionSaving] = useState(false);
  const [captionSaved, setCaptionSaved] = useState(false);
  const captionSaveTimer = useRef(null);
  const lastSavedRef = useRef({ captions: variant.captions || "", hashtags: variant.hashtags || [] });

  // Voiceover-only script edit (cheap: 1cr, TTS + re-mux only — no Kling)
  const [voOpen, setVoOpen] = useState(false);
  const [voScript, setVoScript] = useState(variant.script || "");
  const [voVoice, setVoVoice] = useState(variant.voiceover_voice || "nova");
  const [voLoading, setVoLoading] = useState(false);

  // Lazy-loaded blobs — fetched once from dedicated endpoints instead of embedding in project doc
  const [videoSrc, setVideoSrc] = useState(null);
  const [voiceoverB64, setVoiceoverB64] = useState(null);

  useEffect(() => {
    if (variant.video_status === "completed" && variant.variant_id) {
      axios.get(`${API}/projects/${projectId}/variants/${variant.variant_id}/video`, { withCredentials: true })
        .then(res => { if (res.data?.video_base64) setVideoSrc(`data:video/mp4;base64,${res.data.video_base64}`); })
        .catch(() => {});
    }
    if (variant.voiceover_status === "completed" && variant.variant_id) {
      axios.get(`${API}/projects/${projectId}/variants/${variant.variant_id}/voiceover`, { withCredentials: true })
        .then(res => { if (res.data?.voiceover_base64) setVoiceoverB64(res.data.voiceover_base64); })
        .catch(() => {});
    }
  }, [variant.variant_id, variant.video_status, variant.voiceover_status, projectId]);

  const videoUrl = `${API}/projects/${projectId}/download-video/${variant.variant_id}`;

  // Sync local caption state when variant (or its captions/hashtags) changes — e.g. after A/B apply
  useEffect(() => {
    setCaptionText(variant.captions || "");
    setHashtagList(variant.hashtags || []);
    lastSavedRef.current = { captions: variant.captions || "", hashtags: variant.hashtags || [] };
  }, [variant.variant_id, variant.captions, variant.hashtags]);

  // Sync voiceover-editor state when variant's script or voice changes (e.g. after regen)
  useEffect(() => {
    setVoScript(variant.script || "");
    setVoVoice(variant.voiceover_voice || "nova");
  }, [variant.variant_id, variant.script, variant.voiceover_voice]);

  // Sync the duration slider with the latest video length on the variant (after Remake completes)
  useEffect(() => {
    setRegenDuration(variant.video_duration || 10);
  }, [variant.variant_id, variant.video_duration]);

  // Debounced persist for caption + hashtags
  useEffect(() => {
    const last = lastSavedRef.current;
    const dirty = captionText !== last.captions || JSON.stringify(hashtagList) !== JSON.stringify(last.hashtags);
    if (!dirty) return;
    clearTimeout(captionSaveTimer.current);
    captionSaveTimer.current = setTimeout(async () => {
      setCaptionSaving(true);
      try {
        await axios.patch(
          `${API}/projects/${projectId}/variants/${variant.variant_id}/caption`,
          { captions: captionText, hashtags: hashtagList },
          { withCredentials: true }
        );
        lastSavedRef.current = { captions: captionText, hashtags: [...hashtagList] };
        setCaptionSaved(true);
        setTimeout(() => setCaptionSaved(false), 1500);
      } catch {
        toast.error(t("project.toasts.captionSaveFailed"));
      } finally {
        setCaptionSaving(false);
      }
    }, 800);
    return () => clearTimeout(captionSaveTimer.current);
  }, [captionText, hashtagList, projectId, variant.variant_id]);

  const addHashtag = () => {
    const raw = hashtagInput.trim().replace(/^#+/, "");
    if (!raw) return;
    const tag = `#${raw}`;
    if (hashtagList.includes(tag)) { setHashtagInput(""); return; }
    setHashtagList([...hashtagList, tag]);
    setHashtagInput("");
  };
  const removeHashtag = (tag) => setHashtagList(hashtagList.filter(h => h !== tag));

  const copyCaption = () => {
    const text = `${captionText}${hashtagList.length ? "\n\n" + hashtagList.join(" ") : ""}`.trim();
    if (!text) {
      toast.error(t("project.toasts.nothingToCopy"));
      return;
    }
    navigator.clipboard.writeText(text);
    toast.success(t("project.toasts.captionCopied"));
  };

  const downloadVideo = () => {
    const a = document.createElement("a");
    a.href = videoUrl; a.click();
    toast.success(t("project.toasts.downloading"));
  };

  // Cheap voiceover-only edit — 1 credit, keeps the existing video footage
  const handleUpdateVoiceover = async () => {
    const trimmed = voScript.trim();
    if (!trimmed) {
      toast.error(t("project.toasts.scriptEmpty"));
      return;
    }
    if (trimmed === (variant.script || "").trim() && voVoice === variant.voiceover_voice) {
      toast.info(t("project.toasts.noChanges"));
      return;
    }
    setVoLoading(true);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/variants/${variant.variant_id}/update-voiceover`,
        { script: trimmed, voice: voVoice },
        { withCredentials: true }
      );
      toast.success(res.data.message || t("project.toasts.voiceoverStarted"));
      setVoOpen(false);
      // onRefresh = refreshAndPoll — auto-resumes polling while the regen runs
      if (onRefresh) onRefresh();
    } catch (err) {
      if (handleCreditErr(err)) { setVoLoading(false); return; }
      toast.error(apiErrMsg(err, t("project.toasts.voiceoverFailed")));
    } finally {
      setVoLoading(false);
    }
  };

  const handleGenerateABPack = async () => {
    setAbLoading(true);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/generate-ab-pack`,
        { variant_id: variant.variant_id },
        { withCredentials: true }
      );
      setAbVariants(res.data.variants || []);
      toast.success(t("project.toasts.abReady", { count: res.data.variants?.length || 0 }));
    } catch (err) {
      if (handleCreditErr(err)) { setAbLoading(false); return; }
      toast.error(apiErrMsg(err, t("project.toasts.abFailed")));
    } finally {
      setAbLoading(false);
    }
  };

  const handleApplyAB = async (abVariantId) => {
    setAbApplying(abVariantId);
    try {
      await axios.post(`${API}/projects/${projectId}/apply-ab-variant`,
        { ab_variant_id: abVariantId, target_variant_id: variant.variant_id },
        { withCredentials: true }
      );
      toast.info(t("project.toasts.abApplied"));
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(apiErrMsg(err, t("project.toasts.abApplyFailed")));
    } finally { setAbApplying(null); }
  };

  const handleRestoreOriginal = async () => {
    try {
      await axios.post(`${API}/projects/${projectId}/restore-original`,
        { variant_id: variant.variant_id },
        { withCredentials: true }
      );
      toast.success(t("project.toasts.restored"));
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(apiErrMsg(err, t("project.toasts.restoreFailed")));
    }
  };

  const handleRegenVideoClick = async () => {
    setRegenLoading(true);
    try {
      await onRegenVideo(
        variant.variant_id,
        regenVoice,
        regenScript,
        regenHook,
        regenDirection.trim() || null,
        regenDuration,
        regenMusicMood,
        regenBurnCaptions
      );
      setRegenOpen(false);
      setRegenDirection("");
    } finally {
      setRegenLoading(false);
    }
  };

  const downloadAdPack = async () => {
    setDownloadingPack(true);
    try {
      const JSZip = (await import("jszip")).default;
      const { saveAs } = await import("file-saver");
      const zip = new JSZip();
      const slug = project.business_name.replace(/\s+/g, "-").toLowerCase();
      const folder = zip.folder(`${slug}-ad`);

      folder.file("script.txt",
        `HOOK:\n${variant.hook}\n\nVOICEOVER:\n${variant.script}\n\nCTA: ${variant.cta}\n\nSTORYBOARD:\n${variant.scenes?.map(s => `Scene ${s.scene_number}: ${s.description} — "${s.overlay_text}"`).join("\n")}`
      );
      folder.file("caption.txt", `${variant.captions}\n\n${variant.hashtags?.join(" ")}`);

      variant.scenes?.forEach((s, i) => {
        if (s.image_base64) {
          const bytes = new Uint8Array(atob(s.image_base64).split("").map(c => c.charCodeAt(0)));
          folder.file(`scene-${i + 1}.jpg`, bytes, { binary: true });
        }
      });

      if (voiceoverB64) {
        const bytes = new Uint8Array(atob(voiceoverB64).split("").map(c => c.charCodeAt(0)));
        folder.file("voiceover.mp3", bytes, { binary: true });
      }

      try {
        const resp = await fetch(videoUrl, { credentials: "include" });
        if (resp.ok) folder.file("ad-video.mp4", new Uint8Array(await resp.arrayBuffer()), { binary: true });
      } catch (err) { console.warn("Video include in ad pack skipped:", err); }

      saveAs(await zip.generateAsync({ type: "blob" }), `${slug}-ad.zip`);
      toast.success(t("project.toasts.packDownloaded"));
    } catch (err) { console.error("Ad pack download failed:", err); toast.error(t("project.toasts.downloadFailed")); }
    finally { setDownloadingPack(false); }
  };

  return (
    <div className="max-w-xl mx-auto text-center">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
        <div className="flex justify-center mb-5">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: "rgba(134,239,172,0.1)", border: "1px solid rgba(134,239,172,0.2)" }}>
            <Check className="w-8 h-8 text-green-400" />
          </div>
        </div>
        <h2 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "Epilogue, sans-serif" }}>
          {t("project.ready.title")}
        </h2>
        <p className="text-sm text-zinc-400 mb-6">{t("project.ready.subtitle")}</p>

        {/* Inline Video Player */}
        {videoSrc && (
          <div className="mb-6 rounded-2xl overflow-hidden" style={{ border: "1px solid #2a2a2a" }}>
            <video
              src={videoSrc}
              controls
              loop
              playsInline
              className="w-full"
              style={{ maxHeight: 480, background: "#0a0a0a" }}
              data-testid="ad-video-player"
            />
          </div>
        )}

        <div className="flex gap-3 justify-center flex-wrap mb-8">
          <button onClick={downloadVideo}
            data-testid="download-video-ready-btn"
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm text-white transition-all"
            style={{ background: "linear-gradient(135deg, #7c3aed, #9333ea)" }}>
            <Download className="w-4 h-4" /> {t("project.ready.downloadMp4")}
          </button>
          <button onClick={downloadAdPack} disabled={downloadingPack}
            data-testid="download-pack-ready-btn"
            className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all"
            style={{ background: "#1c1c1c", border: "1px solid #2a2a2a", color: "#a0a0a0" }}>
            {downloadingPack ? <Loader className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
            {downloadingPack ? t("project.ready.packaging") : t("project.ready.fullPack")}
          </button>
          <button onClick={() => setShowPublish(true)}
            data-testid="publish-now-btn"
            className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all"
            style={{ background: "#1c1c1c", border: "1px solid rgba(168,85,247,0.3)", color: "#c084fc" }}>
            <Share2 className="w-4 h-4" /> {t("project.ready.publish")}
          </button>
          <button onClick={() => setShowSchedule(true)}
            data-testid="schedule-post-btn"
            className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all"
            style={{ background: "#1c1c1c", border: "1px solid #2a2a2a", color: "#a0a0a0" }}>
            <Calendar className="w-4 h-4" /> {t("project.ready.schedule")}
          </button>
        </div>

        {/* Caption — editable */}
        <div className="rounded-2xl p-5 mb-6 text-left"
          style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: "#6b6b6b" }}>{t("project.ready.caption")}</p>
              {captionSaving && <span className="text-[10px]" style={{ color: "#6b6b6b" }}>{t("common.saving")}</span>}
              {captionSaved && !captionSaving && <span className="text-[10px]" style={{ color: "#86efac" }}>{t("project.ready.saved")}</span>}
            </div>
            <button onClick={copyCaption}
              className="text-xs hover:text-white transition-colors flex items-center gap-1"
              style={{ color: "#c084fc" }}
              data-testid="copy-caption-ready">
              <Copy className="w-3 h-3" /> {t("common.copy")}
            </button>
          </div>
          <textarea
            value={captionText}
            onChange={e => setCaptionText(e.target.value)}
            rows={4}
            dir="auto"
            placeholder={t("project.ready.captionPlaceholder")}
            data-testid="caption-editor"
            className="w-full text-sm text-white rounded-xl p-3 resize-none focus:outline-none focus:ring-1 focus:ring-purple-500 leading-relaxed"
            style={{ background: "#161616", border: "1px solid #2a2a2a" }}
          />

          <div className="mt-3">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "#6b6b6b" }}>{t("project.ready.hashtags")}</p>
            <div className="flex flex-wrap gap-1.5 items-center">
              {hashtagList.map(tag => (
                <span key={tag}
                  data-testid={`hashtag-chip-${tag.replace(/[^a-zA-Z0-9]/g, "")}`}
                  className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1 group"
                  style={{ background: "rgba(168,85,247,0.08)", color: "#c084fc", border: "1px solid rgba(168,85,247,0.15)" }}>
                  {tag}
                  <button onClick={() => removeHashtag(tag)}
                    data-testid={`remove-hashtag-${tag.replace(/[^a-zA-Z0-9]/g, "")}`}
                    className="opacity-50 hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <div className="flex items-center gap-1">
                <input
                  value={hashtagInput}
                  onChange={e => setHashtagInput(e.target.value.replace(/\s+/g, ""))}
                  onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addHashtag(); } }}
                  placeholder={t("project.ready.addHashtag")}
                  data-testid="hashtag-input"
                  className="text-xs px-2 py-0.5 rounded-full focus:outline-none focus:ring-1 focus:ring-purple-500"
                  style={{ background: "#161616", color: "#a0a0a0", border: "1px dashed #333", width: 110 }}
                />
                {hashtagInput.trim() && (
                  <button onClick={addHashtag}
                    data-testid="add-hashtag-btn"
                    className="text-xs font-semibold" style={{ color: "#c084fc" }}>{t("project.ready.add")}</button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Voiceover script — cheap edit, no Kling */}
        <div className="rounded-2xl p-5 mb-6 text-left"
          style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Volume2 className="w-4 h-4" style={{ color: "#c084fc" }} />
              <p className="text-sm font-bold text-white">{t("project.ready.voScript")}</p>
              <CreditBadge credits={1} />
            </div>
            <button onClick={() => setVoOpen(o => !o)}
              className="text-xs font-semibold transition-colors" style={{ color: "#c084fc" }}
              data-testid="voiceover-edit-toggle">
              {voOpen ? t("common.cancel") : t("project.ready.editVo")}
            </button>
          </div>
          {!voOpen && (
            <p className="text-xs mt-1" style={{ color: "#6b6b6b" }}>
              {t("project.ready.voHint")}
            </p>
          )}
          {voOpen && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: "#6b6b6b" }}>{t("project.ready.script")}</label>
                <textarea
                  value={voScript}
                  onChange={e => setVoScript(e.target.value)}
                  rows={4}
                  dir="auto"
                  maxLength={1200}
                  data-testid="voiceover-script-input"
                  placeholder={t("project.ready.scriptPlaceholder")}
                  className="w-full text-sm text-white rounded-xl p-3 resize-none focus:outline-none focus:ring-1 focus:ring-purple-500 leading-relaxed"
                  style={{ background: "#161616", border: "1px solid #2a2a2a" }}
                />

                {/* Duration mapping: highlights how much of the script will be heard at the selected video length */}
                {(() => {
                  const WPS = 2.85;  // OpenAI tts-1 average speech rate
                  const videoDur = regenDuration;
                  const maxWords = Math.floor(videoDur * WPS);
                  const words = voScript.trim().split(/\s+/).filter(Boolean);
                  const wordCount = words.length;
                  const estSec = wordCount / WPS;
                  const gap = videoDur - estSec;          // positive = silence; negative = cutoff
                  const fits = Math.abs(gap) <= 0.7;       // within ~0.7s = good
                  const isOverflow = wordCount > maxWords;
                  const isUnderfill = !isOverflow && !fits && gap > 0;

                  const statusColor = fits ? "#86efac" : "#fbbf24";
                  const statusBg    = fits ? "rgba(134,239,172,0.08)" : "rgba(251,191,36,0.08)";
                  const statusLabel = fits
                    ? t("project.fit.perfect", { seconds: videoDur })
                    : isOverflow
                      ? t("project.fit.overflow", { seconds: (estSec - videoDur).toFixed(1) })
                      : t("project.fit.silence", { seconds: gap.toFixed(1) });

                  return (
                    <>
                      <div className="flex items-center justify-between mt-2 text-[10px]" style={{ color: "#6b6b6b" }}>
                        <span data-testid="voiceover-duration-stat">
                          <span style={{ color: "#a0a0a0" }}>{wordCount}</span> {t("project.fit.words")} ·
                          ~<span style={{ color: "#a0a0a0" }}>{estSec.toFixed(1)}s</span> {t("project.fit.audio")} ·
                          {t("project.fit.target")} <span style={{ color: "#a0a0a0" }}>{videoDur}s</span>
                        </span>
                        <span>{voScript.length}/1200</span>
                      </div>

                      <div
                        className="mt-2 px-3 py-1.5 rounded-lg text-[10px] font-semibold flex items-center gap-1.5"
                        style={{ background: statusBg, color: statusColor, border: `1px solid ${statusColor}33` }}
                        data-testid="voiceover-fit-status">
                        {fits ? <Check className="w-3 h-3" /> : <span style={{ fontSize: "0.7rem" }}>⚠</span>}
                        {statusLabel}
                      </div>

                      {/* Visual highlight: show which portion fits inside the selected duration */}
                      {wordCount > 0 && (isOverflow || isUnderfill) && (
                        <div
                          className="mt-2 p-3 rounded-xl text-sm leading-relaxed"
                          style={{ background: "#111", border: "1px dashed #2a2a2a" }}
                          data-testid="voiceover-mapping-preview">
                          <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: "#6b6b6b" }}>
                            {t("project.fit.mappedTo", { seconds: videoDur })}
                          </p>

                          {isOverflow ? (
                            <p dir="auto">
                              <span style={{ color: "#fff" }}>
                                {words.slice(0, maxWords).join(" ")}
                              </span>
                              <span style={{ color: "#fbbf24", padding: "0 6px", fontWeight: 700 }}>
                                {" │ "}<span style={{ fontSize: "0.65rem", letterSpacing: "0.05em" }}>{t("project.fit.cutoff", { seconds: videoDur })}</span>{" │ "}
                              </span>
                              <span style={{ color: "#555", textDecoration: "line-through", textDecorationColor: "#444" }}>
                                {words.slice(maxWords).join(" ")}
                              </span>
                            </p>
                          ) : (
                            <p dir="auto">
                              <span style={{ color: "#fff" }}>
                                {voScript.trim()}
                              </span>
                              <span
                                className="inline-block ml-2 px-2 py-0.5 rounded-full"
                                style={{ background: "rgba(251,191,36,0.12)", color: "#fbbf24", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.05em" }}>
                                {t("project.fit.silenceTag", { seconds: gap.toFixed(1) })}
                              </span>
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Video Length — drives the script highlight and the Remake duration */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold uppercase tracking-wider" style={{ color: "#6b6b6b" }}>{t("project.builder.videoLength")}</label>
                  <span className="text-sm font-black text-white" data-testid="vo-duration-value">{regenDuration}s</span>
                </div>
                <input
                  type="range" min={5} max={30} step={5}
                  value={regenDuration}
                  onChange={e => setRegenDuration(Number(e.target.value))}
                  data-testid="vo-duration-slider"
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #9333ea ${((regenDuration - 5) / 25) * 100}%, #252525 ${((regenDuration - 5) / 25) * 100}%)`,
                    accentColor: "#9333ea"
                  }}
                />
                <div className="flex justify-between mt-1">
                  {[5, 10, 15, 20, 25, 30].map(v => (
                    <button key={v} onClick={() => setRegenDuration(v)} className="text-xs transition-colors"
                      data-testid={`vo-duration-${v}`}
                      style={{ color: regenDuration === v ? "#c084fc" : "#6b6b6b" }}>{v}s</button>
                  ))}
                </div>
                {regenDuration !== (variant.video_duration || 10) && (
                  <p className="text-[10px] mt-2" style={{ color: "#6b6b6b" }} data-testid="vo-duration-mismatch-hint">
                    {t("project.ready.durationMismatchBefore")} <span style={{ color: "#a0a0a0" }}>{variant.video_duration || 10}s</span> {t("project.ready.durationMismatchMid")} <span style={{ color: "#a0a0a0" }}>{regenDuration}s</span>, {t("project.ready.durationMismatchUse")} <span style={{ color: "#c084fc" }}>{t("project.ready.remakeTitle")}</span> {t("project.ready.below")}
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-3 block" style={{ color: "#6b6b6b" }}>
                  {t("project.ready.voice")} <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>({t("project.ready.optionalChange")})</span>
                </label>
                <VoiceSelector value={voVoice} onChange={setVoVoice} />
              </div>

              <button
                onClick={handleUpdateVoiceover}
                disabled={voLoading}
                data-testid="voiceover-update-btn"
                className="w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
                style={{
                  background: voLoading ? "#252525" : "rgba(147,51,234,0.15)",
                  border: "1px solid rgba(147,51,234,0.35)",
                  color: voLoading ? "#6b6b6b" : "#c084fc"
                }}>
                {voLoading
                  ? <><Loader className="w-4 h-4 animate-spin" /> {t("project.ready.starting")}</>
                  : <><Volume2 className="w-4 h-4" /> {t("project.ready.updateVo")} <CreditBadge credits={1} /></>}
              </button>
              <p className="text-[10px] text-center" style={{ color: "#6b6b6b" }}>
                {t("project.ready.updateVoHint")}
              </p>
            </div>
          )}
        </div>

        {/* Remake video */}
        <div className="rounded-2xl p-5 mb-6 text-left"
          style={{ background: "#161616", border: "1px solid #252525" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" style={{ color: "#c084fc" }} />
              <p className="text-sm font-bold text-white">{t("project.ready.remakeTitle")}</p>
              <CreditBadge credits={videoCredits(regenDuration)} />
            </div>
            <button
              onClick={() => setRegenOpen(o => !o)}
              className="text-xs font-semibold transition-colors"
              style={{ color: "#c084fc" }}
              data-testid="regen-video-toggle">
              {regenOpen ? t("common.cancel") : t("project.ready.editRemake")}
            </button>
          </div>
          {!regenOpen && (
            <p className="text-xs mt-1" style={{ color: "#6b6b6b" }}>
              {t("project.ready.remakeHint")}
            </p>
          )}
          {regenOpen && (
            <div className="mt-4 space-y-5">

              {/* Hook */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: "#6b6b6b" }}>{t("project.builder.hook")}</label>
                <textarea
                  value={regenHook}
                  onChange={e => setRegenHook(e.target.value)}
                  rows={2}
                  dir="auto"
                  data-testid="regen-hook-input"
                  placeholder={t("project.ready.hookPlaceholder")}
                  className="w-full text-sm text-white rounded-xl p-3 resize-none focus:outline-none focus:ring-1 focus:ring-purple-500"
                  style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}
                />
              </div>

              {/* Script */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: "#6b6b6b" }}>{t("project.builder.script")}</label>
                <textarea
                  value={regenScript}
                  onChange={e => setRegenScript(e.target.value)}
                  rows={4}
                  dir="auto"
                  data-testid="regen-script-input"
                  placeholder={t("project.ready.narrationPlaceholder")}
                  className="w-full text-sm text-white rounded-xl p-3 resize-none focus:outline-none focus:ring-1 focus:ring-purple-500"
                  style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}
                />
              </div>

              {/* Voice */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-3 block" style={{ color: "#6b6b6b" }}>
                  {t("project.ready.voiceSelectLabel")}
                </label>
                <VoiceSelector value={regenVoice} onChange={setRegenVoice} />
              </div>

              {/* Music */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-3 block" style={{ color: "#6b6b6b" }}>{t("project.builder.music")}</label>
                <MusicSelector value={regenMusicMood} onChange={setRegenMusicMood} />
              </div>

              {/* Duration — controlled by the slider in Voiceover Script panel above */}
              <div className="flex items-center justify-between p-3 rounded-xl"
                style={{ background: "#111", border: "1px solid #252525" }}
                data-testid="remake-duration-readout">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider" style={{ color: "#6b6b6b" }}>{t("project.builder.videoLength")}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: "#6b6b6b" }}>
                    {t("project.ready.changeAbove")} <span style={{ color: "#c084fc" }}>{t("project.ready.voScript")}</span>
                  </p>
                </div>
                <span className="text-lg font-black text-white">{regenDuration}s</span>
              </div>

              {/* Burn Captions */}
              <div className="flex items-center justify-between p-4 rounded-xl"
                style={{ background: "#111", border: "1px solid #252525" }}>
                <div>
                  <p className="text-sm font-bold text-white">{t("project.builder.burnCaptions")}</p>
                  <p className="text-xs mt-0.5" style={{ color: "#6b6b6b" }}>{t("project.ready.burnHint")}</p>
                </div>
                <button
                  onClick={() => setRegenBurnCaptions(b => !b)}
                  data-testid="regen-burn-captions-toggle"
                  className="relative rounded-full transition-all flex-shrink-0"
                  style={{
                    background: regenBurnCaptions ? "#9333ea" : "#252525",
                    width: 40, height: 22,
                    border: `1px solid ${regenBurnCaptions ? "#7c3aed" : "#333"}`
                  }}>
                  <span className="absolute top-0.5 transition-all rounded-full bg-white"
                    style={{ width: 16, height: 16, left: regenBurnCaptions ? 22 : 3 }} />
                </button>
              </div>

              {/* Video Direction */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: "#6b6b6b" }}>
                  {t("project.builder.direction")} <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>({t("common.optional").toLowerCase()})</span>
                </label>
                <textarea
                  value={regenDirection}
                  onChange={e => setRegenDirection(e.target.value)}
                  rows={2}
                  data-testid="regen-direction-input"
                  placeholder={t("project.ready.directionPlaceholder")}
                  className="w-full text-sm text-white rounded-xl p-3 resize-none focus:outline-none focus:ring-1 focus:ring-purple-500"
                  style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}
                />
              </div>

              <button
                onClick={handleRegenVideoClick}
                disabled={regenLoading}
                data-testid="regen-video-btn"
                className="w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
                style={{
                  background: regenLoading ? "#252525" : "rgba(147,51,234,0.15)",
                  border: "1px solid rgba(147,51,234,0.35)",
                  color: regenLoading ? "#6b6b6b" : "#c084fc"
                }}>
                {regenLoading
                  ? <><Loader className="w-4 h-4 animate-spin" /> {t("project.ready.starting")}</>
                  : <><RefreshCw className="w-4 h-4" /> {t("project.ready.remakeVideo")} <CreditBadge credits={videoCredits(regenDuration)} /></>}
              </button>
            </div>
          )}
        </div>

        {/* A/B Pack Generator — Try a different style */}
        <div className="rounded-2xl p-5 mb-6 text-left"
          style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4" style={{ color: "#c084fc" }} />
              <p className="text-sm font-bold text-white">{t("project.ab.title")}</p>
              <CreditBadge credits={2} />
            </div>
          </div>
          <p className="text-xs mb-4" style={{ color: "#6b6b6b" }}>
            {t("project.ab.hint")} <span style={{ color: "#a0a0a0" }}>{t("project.ready.editRemake")}</span> {t("project.ab.hintAfter")}
          </p>
          {variant.has_original_backup && (
            <button onClick={handleRestoreOriginal}
              data-testid="restore-original-btn"
              className="w-full py-2 rounded-xl text-xs font-semibold mb-3 flex items-center justify-center gap-1.5 transition-all"
              style={{ background: "rgba(234,179,8,0.08)", border: "1px solid rgba(234,179,8,0.2)", color: "#fbbf24" }}>
              <RefreshCw className="w-3 h-3" /> {t("project.ab.restore")}
            </button>
          )}
          {abVariants.length === 0 ? (
            <button onClick={handleGenerateABPack} disabled={abLoading}
              data-testid="generate-ab-pack-btn"
              className="w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all"
              style={{ background: abLoading ? "#252525" : "rgba(147,51,234,0.12)", border: "1px solid rgba(147,51,234,0.3)", color: abLoading ? "#6b6b6b" : "#c084fc" }}>
              {abLoading
                ? <><Loader className="w-4 h-4 animate-spin" /> {t("project.ab.generating")}</>
                : <><FlaskConical className="w-4 h-4" /> {t("project.ab.generate")} <CreditBadge credits={2} /></>}
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-semibold mb-2" style={{ color: "#6b6b6b" }}>
                {t("project.ab.pickHint")} <span style={{ color: "#a0a0a0" }}>{t("project.ready.editRemake")}</span> {t("project.ab.pickHintAfter")}
              </p>
              {abVariants.map((v, i) => (
                <div key={v.variant_id} className="rounded-xl p-3 flex items-start gap-3"
                  style={{ background: "#161616", border: "1px solid #252525" }}
                  data-testid={`ab-variant-${i + 1}`}>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full mb-1 inline-block"
                      style={{ background: "rgba(168,85,247,0.1)", color: "#c084fc", border: "1px solid rgba(168,85,247,0.15)" }}>
                      {t(`tones.${v.ab_tone}`, { defaultValue: v.ab_tone })}
                    </span>
                    <p className="text-xs font-semibold text-white mt-1 leading-snug">"{v.hook}"</p>
                    <p className="text-xs leading-relaxed mt-0.5" style={{ color: "#6b6b6b" }}>
                      {v.script?.slice(0, 80)}{v.script?.length > 80 ? "..." : ""}
                    </p>
                  </div>
                  <button onClick={() => handleApplyAB(v.variant_id)}
                    disabled={abApplying === v.variant_id}
                    data-testid={`apply-ab-variant-${i + 1}`}
                    className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                    style={{ background: "rgba(147,51,234,0.12)", border: "1px solid rgba(147,51,234,0.3)", color: "#c084fc" }}>
                    {abApplying === v.variant_id ? <Loader className="w-3 h-3 animate-spin" /> : t("project.ab.useThis")}
                  </button>
                </div>
              ))}
              <button onClick={() => setAbVariants([])}
                className="w-full text-xs mt-1 transition-colors" style={{ color: "#6b6b6b" }}>
                {t("project.ab.regenerate")}
              </button>
            </div>
          )}
        </div>

        {/* Start over */}
        <button onClick={onStartOver}
          className="w-full py-3 rounded-xl font-semibold text-sm transition-all hover:text-white"
          style={{ background: "#161616", border: "1px solid #252525", color: "#a0a0a0" }}
          data-testid="start-over-btn">
          {t("project.ready.startOver")}
        </button>
      </motion.div>

      {showPublish && (
        <PublishModal
          project={project}
          variant={variant}
          projectId={projectId}
          onClose={() => setShowPublish(false)}
        />
      )}
      {showSchedule && (
        <ScheduleModal
          project={project}
          variant={variant}
          onClose={() => setShowSchedule(false)}
          onScheduled={() => {}}
        />
      )}
    </div>
  );
}

// ========== MAIN ==========
export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Handle credit / subscription errors — redirect to billing with a clear message
  const handleCreditErr = (err) => {
    if (isSubscriptionError(err)) {
      toast.error(t("project.toasts.subscriptionRedirect"), { duration: 3500 });
      setTimeout(() => navigate("/billing"), 1200);
      return true;
    }
    if (isCreditError(err)) {
      toast.error(t("project.toasts.creditsRedirect"), { duration: 3000 });
      setTimeout(() => navigate("/billing"), 1200);
      return true;
    }
    return false;
  };
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSettings, setEditingSettings] = useState(false);
  const [previousVariantId, setPreviousVariantId] = useState(null);
  const pollRef = useRef(null);
  const pollCount = useRef(0);

  const fetchProject = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
      setProject(res.data);
      return res.data;
    } catch {
      toast.error(t("project.toasts.loadFailed"));
      return null;
    }
  }, [projectId]); // eslint-disable-line

  const startPolling = useCallback((shouldContinue) => {
    clearInterval(pollRef.current);
    pollCount.current = 0;
    pollRef.current = setInterval(async () => {
      pollCount.current++;
      if (pollCount.current > 120) { clearInterval(pollRef.current); return; }
      const updated = await fetchProject();
      if (updated && !shouldContinue(updated)) clearInterval(pollRef.current);
    }, 4000);
  }, [fetchProject]);

  const needsPoll = (p) => {
    if (!p) return false;
    if (p.status === "failed" && !p.variants?.length) return false; // text gen failed — stop polling
    if (["pending", "generating"].includes(p.status)) return true;
    const sv = p.variants?.find(v => v.variant_id === p.selected_variant_id);
    if (["starting", "generating_video", "generating_voiceover", "rendering_video"].includes(sv?.full_ad_status)) return true;
    return false;
  };

  // Children call this after mutating something — refetches and auto-resumes polling
  // when a background job is still running. Returns the latest project doc.
  const refreshAndPoll = useCallback(async () => {
    const updated = await fetchProject();
    if (updated && needsPoll(updated)) startPolling(needsPoll);
    return updated;
  }, [fetchProject, startPolling]);

  useEffect(() => {
    const init = async () => {
      const data = await fetchProject();
      // Unfinished wizard draft — resume the wizard; generation only starts from "Generate My Ads"
      if (data?.status === "pending") {
        navigate(`/projects/new?draft=${data.project_id}`, { replace: true });
        return;
      }
      setLoading(false);
      if (!data) return;
      if (needsPoll(data)) startPolling(needsPoll);
    };
    init();
    return () => clearInterval(pollRef.current);
  }, [fetchProject, startPolling, navigate]);

  const handleSelectVariant = async (variantId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/select-variant`,
        { variant_id: variantId }, { withCredentials: true });
      const updated = await fetchProject();
      if (updated && needsPoll(updated)) startPolling(needsPoll);
    } catch (err) {
      toast.error(apiErrMsg(err, t("project.toasts.selectFailed")));
    }
  };

  const handleBack = async () => {
    setPreviousVariantId(project?.selected_variant_id ?? null);
    try {
      await axios.post(`${API}/projects/${projectId}/reset-selection`, {}, { withCredentials: true });
    } catch (err) {
      console.warn("reset-selection failed (proceeding anyway):", err?.response?.status);
    }
    setProject(p => ({ ...p, selected_variant_id: null }));
  };

  const handleRegenerate = async () => {
    // Costs 2 credits but is NOT destructive — adds 3 new concepts to your existing ones.
    if (!window.confirm(t("project.confirm.moreConcepts"))) {
      return;
    }
    try {
      toast.info(t("project.toasts.moreConcepts"));
      // Don't clear variants — backend appends, UI stays usable while new ones arrive
      setProject(p => ({ ...p, status: "generating", error: null }));
      await axios.post(`${API}/projects/${projectId}/regenerate/text`, {}, { withCredentials: true });
      startPolling(p => ["pending", "generating"].includes(p?.status));
    } catch (err) {
      if (handleCreditErr(err)) return;
      toast.error(t("project.toasts.moreConceptsFailed"));
    }
  };

  // Retry after text generation failure — same as regenerate but no confirm dialog
  const handleRetryTextGen = async () => {
    try {
      setProject(p => ({ ...p, status: "generating", variants: [], selected_variant_id: null, error: null }));
      await axios.post(`${API}/projects/${projectId}/regenerate/text`, {}, { withCredentials: true });
      startPolling(p => ["pending", "generating"].includes(p?.status));
    } catch (err) {
      if (handleCreditErr(err)) return;
      toast.error(t("project.toasts.retryFailed"));
    }
  };

  const handleGenerateFullAd = async (config) => {
    try {
      await axios.post(`${API}/projects/${projectId}/generate-full-ad`, config, { withCredentials: true });
      toast.info(t("project.toasts.generatingAd"));
      const updated = await fetchProject();
      if (updated && needsPoll(updated)) startPolling(needsPoll);
    } catch (err) {
      if (handleCreditErr(err)) { throw err; }
      toast.error(apiErrMsg(err, t("project.toasts.adFailed")));
      throw err;
    }
  };

  const handleRegenVideo = async (variantId, voice, script, hook, newDirection, videoDuration = 8, musicMood = null, burnCaptions = false) => {
    try {
      await axios.post(
        `${API}/projects/${projectId}/regenerate-video`,
        {
          variant_id: variantId,
          voice,
          script,
          hook,
          veo_custom_prompt: newDirection || null,
          video_duration: videoDuration,
          music_mood: musicMood,
          burn_captions: burnCaptions,
        },
        { withCredentials: true }
      );
      toast.info(t("project.toasts.remixingVideo"));
      const updated = await fetchProject();
      if (updated && needsPoll(updated)) startPolling(needsPoll);
    } catch (err) {
      if (handleCreditErr(err)) return;
      toast.error(apiErrMsg(err, t("project.toasts.remixFailed")));
    }
  };

  const handleSettingsSaved = (updatedProject) => {
    setEditingSettings(false);
    setProject(updatedProject);
    startPolling(p => ["pending", "generating"].includes(p?.status));
  };

  const handleStartOver = async () => {
    const prevId = project.selected_variant_id;
    try {
      await axios.post(`${API}/projects/${projectId}/reset-selection`, {}, { withCredentials: true });
    } catch (err) {
      console.warn("reset-selection failed (proceeding anyway):", err?.response?.status);
    }
    setPreviousVariantId(prevId);
    setProject(p => ({ ...p, selected_variant_id: null }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <Loader className="w-8 h-8 text-purple-400 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!project) return null;

  const phase = computePhase(project);
  const selectedVariant = project.variants?.find(v => v.variant_id === project.selected_variant_id);

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
        <button onClick={() => navigate("/dashboard")}
          data-testid="back-to-dashboard-btn"
          className="p-2 rounded-lg transition-colors hover:bg-white/5"
          style={{ color: "#6b6b6b" }}>
          <ArrowLeft className="w-5 h-5 rtl:rotate-180" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-black text-white truncate" style={{ fontFamily: "Epilogue, sans-serif" }}>
            {project.business_name}
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-sm" style={{ color: "#6b6b6b" }}>{t(`industries.${project.industry}`, { defaultValue: project.industry })}</span>
            <span style={{ color: "#6b6b6b" }}>·</span>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ background: "rgba(168,85,247,0.1)", color: "#c084fc", border: "1px solid rgba(168,85,247,0.2)" }}>
              {t(`tones.${project.tone}`, { defaultValue: project.tone })}
            </span>
            {project.platforms?.map(p => (
              <span key={p} className="text-xs px-2 py-0.5 rounded-full"
                style={{ background: "#252525", color: "#a0a0a0", border: "1px solid #333" }}>{p}</span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setEditingSettings(s => !s)}
            data-testid="edit-settings-btn"
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-all"
            style={{
              background: editingSettings ? "rgba(147,51,234,0.12)" : "#1c1c1c",
              border: `1px solid ${editingSettings ? "#9333ea" : "#2a2a2a"}`,
              color: editingSettings ? "#c084fc" : "#6b6b6b"
            }}>
            <Settings className="w-3.5 h-3.5" /> {t("project.editStyle")}
          </button>
          {phase === "select_variant" && project.variants?.length > 0 && (
            <button onClick={handleRegenerate}
              disabled={project.status === "generating"}
              className="flex items-center gap-1.5 text-xs transition-colors hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: "#6b6b6b" }}
              data-testid="regenerate-concepts-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${project.status === "generating" ? "animate-spin" : ""}`} />
              {project.status === "generating" ? t("project.generating") : <>{t("project.moreConcepts")} <CreditBadge credits={2} /></>}
            </button>
          )}
        </div>
      </div>

      {/* Edit Settings Panel */}
      <AnimatePresence>
        {editingSettings && (
          <EditSettingsPanel
            project={project}
            onSaved={handleSettingsSaved}
            onCancel={() => setEditingSettings(false)}
          />
        )}
      </AnimatePresence>

      <div className="mb-8" />

      <AnimatePresence mode="wait">
        {phase === "generating_text" && (
          <motion.div key="gen_text" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <GeneratingTextPhase />
          </motion.div>
        )}

        {phase === "text_failed" && (
          <motion.div key="text_failed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <TextFailedPhase
              error={project?.error}
              onRetry={handleRetryTextGen}
              isPending={project?.status === "pending"}
            />
          </motion.div>
        )}

        {phase === "select_variant" && (
          <motion.div key="select" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-white" style={{ fontFamily: "Epilogue, sans-serif" }}>
                  {t("project.chooseTitle")}
                </h2>
                <p className="text-sm mt-1" style={{ color: "#6b6b6b" }}>
                  {t("project.chooseSubtitle")}
                </p>
              </div>
              {previousVariantId && (
                <button
                  data-testid="back-to-my-ad-btn"
                  onClick={async () => {
                    await handleSelectVariant(previousVariantId);
                    setPreviousVariantId(null);
                  }}
                  className="flex items-center gap-2 text-sm px-4 py-2 rounded-xl flex-shrink-0 transition-colors"
                  style={{ background: "#1c1c1c", border: "1px solid #2a2a2a", color: "#c084fc" }}
                  onMouseOver={e => e.currentTarget.style.borderColor = "#7c3aed"}
                  onMouseOut={e => e.currentTarget.style.borderColor = "#2a2a2a"}>
                  <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("project.backToMyAd")}
                </button>
              )}
            </div>

            {project.status === "generating" && (
              <div
                data-testid="appending-concepts-banner"
                className="mb-5 px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-sm"
                style={{ background: "rgba(168,85,247,0.06)", border: "1px solid rgba(168,85,247,0.2)", color: "#c084fc" }}>
                <Loader className="w-4 h-4 animate-spin" />
                {t("project.appendingBanner")}
              </div>
            )}
            <div className="grid lg:grid-cols-3 gap-5">
              {project.variants.map((variant, i) => (
                <VariantSelectionCard
                  key={variant.variant_id}
                  variant={variant}
                  index={i}
                  onSelect={handleSelectVariant}
                  isCurrentVideo={variant.variant_id === previousVariantId}
                />
              ))}
            </div>
          </motion.div>
        )}

        {phase === "build_ad" && selectedVariant && (
          <motion.div key="build" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <AdBuilderPhase
              variant={selectedVariant}
              project={project}
              onGenerate={handleGenerateFullAd}
              onBack={handleBack}
              onRefresh={fetchProject}
            />
          </motion.div>
        )}

        {phase === "generating_full" && selectedVariant && (
          <motion.div key="gen_full" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <GeneratingFullPhase fullAdStatus={selectedVariant.full_ad_status} startedAt={selectedVariant.full_ad_started_at} />
          </motion.div>
        )}

        {phase === "ready" && selectedVariant && (
          <motion.div key="ready" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ReadyPhase
              project={project}
              variant={selectedVariant}
              projectId={projectId}
              onStartOver={handleStartOver}
              onRegenVideo={handleRegenVideo}
              onRefresh={refreshAndPoll}
              handleCreditErr={handleCreditErr}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}
